import { Store } from '@ngrx/store';
import { delay, filter, first, interval, of, switchMap } from 'rxjs';
import { inject } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { AppStore } from '@aaa/emember/store-types';
import { isMembershipInitialized, MembershipActions } from '@aaa/emember/store-membership';
import { CookiesService } from '../share/services/cookies.service';
import { AvaDialogService } from '../share/services/ava-dialog.service';
import { ClubApp } from '@aaa/emember/types';
import { ZipcodeForm } from './components/zipcode-form/zipcode-form';
import { isSessionInitialized, SessionActions } from '@aaa/emember/store-session';
import { environment } from '../../../environments/environment';
import { GoogleTagManagerService } from '../share/services/google-tag-manager.service';
import { LocationRefService } from '../share/services/location-ref.service';
import { WindowRefService } from '../share/services/window-ref.service';
import { MaiPixelService } from '../share/services/mai-pixel.service';

const MODAL_CLASS_SMALL = 'ava-dialog-small';
const DELAY_TIME = 10;
const CHECK_INTERVAL = 500;
const META_READY_TIMEOUT_MS = 2000;
const META_CHECK_INTERVAL = 50;

export function membershipInitializing() {
  const dialog = inject(AvaDialogService);
  const store = inject(Store<AppStore>);
  const location = inject(LocationRefService);
  const cookies = inject(CookiesService);
  const googleTagManager = inject(GoogleTagManagerService);
  const maiService = inject(MaiPixelService);
  const windowRef = inject(WindowRefService);

  // metaData is injected by the host (e.g. CMS preview) and can arrive after bootstrap.
  // Treat user as "ready" only when it exists and has at least one non-empty value.
  function isMetaDataReady() {
    const user = windowRef.nativeWindow?.metaData?.user;
    if (!user) return false;
    return Object.values(user).some((value) => typeof value === 'string' && value.length > 0);
  }

  function waitForMetaData() {
    const startedAt = Date.now();
    const logWaited = () =>
      console.log(`[ava] waited ${Date.now() - startedAt}ms for window.metaData.user (ready=${isMetaDataReady()})`);

    if (isMetaDataReady()) {
      logWaited();
      return of(true);
    }

    const maxTicks = Math.ceil(META_READY_TIMEOUT_MS / META_CHECK_INTERVAL);

    return interval(META_CHECK_INTERVAL).pipe(
      first((tick) => isMetaDataReady() || tick >= maxTicks),
      tap(() => logWaited()),
      map(() => true),
    );
  }

  function updateZipcodeFromCookie(clubId: ClubApp) {
    return of(true).pipe(
      map(() => cookies.getZipInfo()),
      filter((info) => !!info.zipcode && info.clubId === clubId),
      tap(({ zipcode }) => store.dispatch(MembershipActions.setZipcode({ zipcode }))),
      delay(10),
      map(() => true),
    );
  }

  function isZipcodeValid(zipcode: string) {
    return !!zipcode;
  }

  function waitUntilMembershipReady() {
    store.dispatch(MembershipActions.initialize({ env: environment }));

    return store.select(isMembershipInitialized).pipe(first((status) => status));
  }

  function waitUntilSessionReady() {
    const customToken = windowRef.nativeWindow?.metaData?.user?.token || null;

    store.dispatch(SessionActions.initialize({ customToken }));

    return store.select(isSessionInitialized).pipe(first((status) => status));
  }

  function openPopup() {
    const modal = dialog.openComponent({
      nzContent: ZipcodeForm,
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: false,
      nzKeyboard: false,
      nzClassName: MODAL_CLASS_SMALL,
    });
    return modal;
  }

  function ensureZipcodeState(clubId: ClubApp, rClub: string) {
    // Update cookies and state with zipcode from query params
    const zipInfo = cookies.getZipInfo();
    // stateZipcode   |   cookieZipcode
    // --------------------------------
    // +					          -			updateCookie
    // +					          +			updateState
    // -					          +			updateState
    // -					          -			popup

    // set state zipcode from cookies
    if (isZipcodeValid(zipInfo.zipcode) && zipInfo.clubId === clubId) {
      console.log('updateState ', zipInfo.zipcode);
      return of(zipInfo.zipcode).pipe(
        tap((zipcode) => store.dispatch(MembershipActions.setZipcode({ zipcode }))),
        delay(DELAY_TIME),
        map(() => true),
      );
    }

    // check additional rClub parameter
    if (!isZipcodeValid(zipInfo.zipcode) && rClub === clubId) {
      return of(true).pipe(
        tap(() => cookies.setZipCode(environment.zipcode, rClub)),
        tap(() => store.dispatch(MembershipActions.setZipcode({ zipcode: environment.zipcode }))),
        delay(DELAY_TIME),
      );
    }

    // # Rollback changes WSSV3-1143
    if ([ClubApp.Northway].includes(clubId)) {
      const modal = openPopup();
      return interval(CHECK_INTERVAL).pipe(
        switchMap(() => updateZipcodeFromCookie(clubId)),
        first(),
        tap(() => modal.close()),
      );
    }

    console.log('wait for cookies zipcode');
    return interval(CHECK_INTERVAL).pipe(
      switchMap(() => updateZipcodeFromCookie(clubId)),
      first(),
    );
  }

  return () =>
    of(true).pipe(
      // Wait for host-injected metaData (retry every 50ms, up to 2s) before reading it
      switchMap(() => waitForMetaData()),
      // Add tag manager
      tap(() => googleTagManager.init(environment.googleTagManager)),
      // Add Pixel script
      tap(() => maiService.init(environment.pixelToken)),
      // Wait until membership initialized
      switchMap(waitUntilMembershipReady),
      // Wait until session initialized
      switchMap(waitUntilSessionReady),
      // Checking zip code
      switchMap(() => ensureZipcodeState(environment.clubId, location.getRClubParam())),
    );
}
